<template>
  <!-- Contenido de la página -->
  <div class="beneficios-page" data-aos="fade-up" data-aos-duration="1200">
    <div class="beneficios-title">
      <h1>{{ currentStepData.title }}</h1>
    </div>

    <!-- Stepper -->
    <div class="beneficios-stepper">
      <div v-for="(step, idx) in steps" :key="idx" class="step">
        <div :class="['step-circle', { active: idx <= stepIndex }]" />
        <span class="step-label">{{ stepLabel }} {{ idx + 1 }}</span>
        <div v-if="idx < steps.length - 1"
             class="step-line"
             :class="{ active: idx < stepIndex }"
        ></div>
      </div>
    </div>

    <!-- Contenido del paso -->
    <transition name="fade-slide-beneficio" mode="out-in">
      <div class="beneficios-content row mx-auto" :key="stepIndex">
        <div class="beneficios-text col-12 col-md-6 d-flex flex-column justify-content-center">
          <h2>{{ currentStepData.stepTitle }}</h2>
          <pre class="beneficios-desc">{{ currentStepData.description }}</pre>
        </div>

        <div class="beneficios-img-col col-12 col-md-6 d-flex flex-column align-items-center">
          <div class="beneficios-img-placeholder">
            <img :src="currentStepData.imgSrc"
                 :alt="currentStepData.imgAlt"
                 style="width:100%;height:100%;object-fit:contain;"
            />
          </div>
          <div class="beneficios-img-caption">
            <small>{{ currentStepData.imgCaption }}</small>
          </div>
        </div>
      </div>
    </transition>

    <!-- Navegación -->
    <div class="beneficios-nav">
      <button @click="prevStep" :disabled="stepIndex === 0">{{ prevLabel }}</button>
      <button @click="nextStep" :disabled="stepIndex === steps.length - 1">{{ nextLabel }}</button>
    </div>
  </div>

  <!-- Footer FUERA del wrapper para que no herede estilos -->
 <SiteFooter :selectedLang="selectedLang" />
</template>

<script>
import { useSEO } from '@/composables/useSEO';
import { beneficiosSteps } from '@/assets/data/data.ts'
import SiteFooter from '@/components/SiteFooter.vue'
import AOS from 'aos'
import 'aos/dist/aos.css'

const labels = {
  es: { step: 'Paso', prev: 'Anterior', next: 'Siguiente' },
  en: { step: 'Step', prev: 'Previous', next: 'Next' },
  de: { step: 'Schritt', prev: 'Zurück', next: 'Weiter' }
}

export default {
  name: 'BeneficiosPage',
  components: { SiteFooter },
  props: {
    selectedLang: { type: String, default: 'es' }
  },
  setup() {
    useSEO({
      title: 'Beneficios de la Automatización Industrial',
      description: 'Conoce los beneficios de implementar Industria 4.0 y automatización en tu planta. Reduce costos, mejora calidad y aumenta productividad con Frenkel Engineering.',
      path: '/beneficios',
    });
  },
  data: () => ({ stepIndex: 0 }),
  computed: {
    steps () { return beneficiosSteps[this.selectedLang] || beneficiosSteps.es },
    currentStepData () { return this.steps[this.stepIndex] },
    stepLabel () { return labels[this.selectedLang]?.step || labels.es.step },
    prevLabel () { return labels[this.selectedLang]?.prev || labels.es.prev },
    nextLabel () { return labels[this.selectedLang]?.next || labels.es.next }
  },
  methods: {
    nextStep () { if (this.stepIndex < this.steps.length - 1) this.stepIndex++ },
    prevStep () { if (this.stepIndex > 0) this.stepIndex-- }
  },
  mounted () {
    AOS.init({ duration: 800, once: true })
    setTimeout(() => AOS.refresh(), 300)
  }
}
</script>

<style scoped>
/* ======================================
   ESTILOS PRINCIPALES - BENEFICIOS PAGE
   ====================================== */
.beneficios-page {
  width: 100%;
  background: #f6fafb;
  font-family: 'IBM Plex Mono', monospace;
  padding: 50px 0 32px; /* no empuja el footer */
}

/* ---------- TÍTULO ---------- */
.beneficios-title {
  margin: 2.5rem 0 1.5rem;
  text-align: center;
}
.beneficios-title h1 {
  font-family: 'Caveat-Bold', cursive;
  font-size: 2.3rem;
  font-weight: 700;
  margin: 0;
  position: relative;
  display: inline-block;
  transition: color 0.3s ease;

}
.beneficios-title h1::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 0;
  background-color: currentColor;
  transition: width 0.3s ease;
   
}
.beneficios-title h1:hover::after {
  width: 100%;
}

/* ---------- STEPPER ---------- */
.beneficios-stepper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
  padding: 0 1rem;
}
.step {
  display: flex;
  align-items: center;
}
.step-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 3px solid #222;
  background: #fff;
  transition: 0.3s;
  flex-shrink: 0;
}
.step-circle.active {
  background: #222;
  border-color: #222;
}
.step-label {
  margin: 0 10px 0 6px;
  font-size: 1.05rem;
  white-space: nowrap;
}
.step-line {
  width: 60px;
  height: 4px;
  background: #e5e5e5;
  border-radius: 2px;
  margin: 0 6px;
  flex-shrink: 0;
}
.step-line.active {
  background: #222;
}

/* ---------- CONTENIDO ---------- */
.beneficios-content {
  max-width: 1100px;
  margin: 0 auto 1.5rem;
}
.beneficios-text {
  padding-right: 1rem;
  min-width: 220px;
  position: relative;
  text-align: justify;
}
.beneficios-text h2 {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 1rem;
}
.beneficios-desc {
  font-size: 1rem;
  background: none;
  border: none;
  white-space: pre-wrap;
  margin: 0;
  padding: 0;
  color: #333;
}
.beneficios-text::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 0;
  height: 3px;
  background: #222;
  transition: width 0.4s ease;
  opacity: 0.2;
}
.beneficios-text:hover::after {
  width: 100%;
  opacity: 0.2;
}

/* ---------- IMAGEN ---------- */
.beneficios-img-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 220px;
}
.beneficios-img-placeholder {
  border: 3px solid #222;
  width: 100%;
  max-width: 570px;
  height: 410px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
}
.beneficios-img-placeholder:hover {
  transform: scale(1.03);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
}
.beneficios-img-caption {
  margin-top: 7px;
  text-align: justify;
  font-size: 0.74rem;
  color: #555;
}

/* ---------- NAVEGACIÓN ---------- */
.beneficios-nav {
  display: flex;
  justify-content: center;
  gap: 1.3rem;
  margin-top: 18px;
  flex-wrap: wrap;
}
.beneficios-nav button {
  background: #fff;
  border: 1.8px solid #222;
  border-radius: 8px;
  padding: 7px 30px;
  font-size: 1.02rem;
  color: #222;
  cursor: pointer;
  transition: background 0.15s;
}
.beneficios-nav button:hover:not([disabled]) {
  background: #222;
  color: #fff;
}
.beneficios-nav button[disabled] {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ---------- TRANSICIONES ---------- */
.fade-slide-beneficio-enter-active,
.fade-slide-beneficio-leave-active {
  transition: all 0.45s ease;
  position: relative;
}
.fade-slide-beneficio-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-slide-beneficio-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* ---------- RESPONSIVE DESKTOP PEQUEÑO ---------- */
@media (max-width: 900px) {
  .beneficios-img-placeholder {
    width: 100%;
    max-width: 92vw;
    height: 220px;
  }
}

/* ======================================
   OPTIMIZACIÓN UX/UI PARA MÓVILES
   ====================================== */
@media (max-width: 768px) {

  /* Estructura general */
  .beneficios-page {
    padding: 32px 16px 40px;
  }

  .beneficios-title h1 {
    font-size: 1.6rem;
    margin-bottom: 1.8rem;
  }

  /* Stepper compacto */
  .beneficios-stepper {
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    margin-bottom: 1.5rem;
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 60px;
  }

  .step-circle {
    width: 20px;
    height: 20px;
    border-width: 2px;
  }

  .step-label {
    font-size: 0.85rem;
    margin-top: 4px;
  }

  .step-line {
    display: none;
  }

  /* Contenido */
  .beneficios-content {
    flex-direction: column;
    text-align: justify;
    max-width: 95vw;
  }

  .beneficios-text {
    padding: 0;
    margin-bottom: 1rem;
  }

  .beneficios-text h2 {
    font-size: 1rem;
    margin-bottom: 0.7rem;
  }

  .beneficios-desc {
    font-size: 0.95rem;
    line-height: 1.45;
    color: #333;
  }

  /* Imagen */
  .beneficios-img-col {
    width: 100%;
  }

  .beneficios-img-placeholder {
    border-width: 2px;
    width: 100%;
    height: auto;
    max-width: 100%;
    aspect-ratio: 16 / 10;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  }

  .beneficios-img-caption {
    font-size: 0.75rem;
    color: #777;
    margin-top: 6px;
  }

  /* Navegación */
  .beneficios-nav {
    margin-top: 1.4rem;
    gap: 0.8rem;
  }

  .beneficios-nav button {
    width: 42%;
    min-width: 120px;
    padding: 8px 0;
    font-size: 0.9rem;
    border-radius: 6px;
  }
}

/* ---------- MICRO AJUSTE EXTRA PEQUEÑOS (≤480px) ---------- */
@media (max-width: 480px) {
  .beneficios-title h1 {
    font-size: 1.45rem;
  }

  .beneficios-stepper {
    gap: 10px;
  }

  .step {
    width: 50px;
  }

  .beneficios-desc {
    font-size: 0.9rem;
  }

  .beneficios-nav button {
    font-size: 0.85rem;
    padding: 7px 0;
  }
}
</style>
